import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Period } from '@spike/model';
import clsx from 'clsx';
import Button from 'components/UI/V3/Button/Button';
import { DatePicker } from 'components/UI/V3/DatePicker/DatePicker';
import styles from 'components/UI/V3/DateRangePicker/DateRangePicker.module.scss';
import { Moment } from 'moment-timezone';
import React from 'react';

export type DateRangePickerOption = {
    label: string;
    value: Period | null;
};

interface DateRangePickerProps {
    minDate?: Moment;
    maxDate?: Moment;
    timeZone: string;
    options?: Array<DateRangePickerOption>;
    value?: Period | null;
    onClose?: () => void;
    onChange?: (value: Period | null) => void;
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
    minDate,
    maxDate,
    timeZone,
    options,
    value,
    onChange,
    onClose
}) => {
    const [selectedRange, setSelectedRange] = React.useState<Period | null>(value ?? null);

    const isOptionSelected = React.useCallback(
        (option: DateRangePickerOption) => {
            if (value === null && option.value === null) {
                return true;
            }

            if (value === null || option.value === null) {
                return false;
            }

            return value?.from.isSame(option.value?.from, 'day') && value?.to.isSame(option.value?.to, 'day');
        },
        [value]
    );

    const isOptionDisabled = React.useCallback(
        (option: DateRangePickerOption) => {
            if (option.value === null || !minDate || !maxDate) {
                return false;
            }

            return option.value?.from.isBefore(minDate) || option.value?.to.isAfter(maxDate);
        },
        [minDate, maxDate]
    );

    React.useEffect(() => {
        setSelectedRange(value ?? null);
    }, [value]);

    return (
        <div className={styles.root}>
            <div className={styles.header}>
                <p className={styles.title}>Choose Date</p>

                {onClose && (
                    <button
                        type="button"
                        className={styles.closeButton}
                        onClick={onClose}
                    >
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                )}
            </div>

            <div className={styles.dateOptions}>
                {options?.map(option => (
                    <button
                        type="button"
                        key={option.label}
                        className={clsx(styles.dateOption, {
                            [styles.selected]: isOptionSelected(option)
                        })}
                        disabled={isOptionDisabled(option)}
                        onClick={() => onChange?.(option.value)}
                    >
                        {option.label}
                    </button>
                ))}
            </div>

            <div className={styles.datePicker}>
                <div className={styles.datePickerHeader}>
                    <div className={styles.datePickerHeaderItem}>
                        <label htmlFor="startDate">FROM</label>
                        <input
                            type="text"
                            id="startDate"
                            value={selectedRange?.from.format('MMM DD, YYYY')}
                        />
                    </div>

                    <div className={styles.datePickerHeaderItem}>
                        <label htmlFor="endDate">TO</label>
                        <input
                            type="text"
                            id="endDate"
                            value={selectedRange?.to.format('MMM DD, YYYY')}
                        />
                    </div>
                </div>

                <DatePicker
                    rangeMode
                    minDate={minDate}
                    maxDate={maxDate}
                    timeZone={timeZone}
                    value={selectedRange}
                    onChange={setSelectedRange}
                    className={styles.datePickerCalendar}
                />

                <div className={styles.datePickerFooter}>
                    <Button
                        label="Apply"
                        variant="green"
                        onClick={() => onChange?.(selectedRange)}
                    />
                </div>
            </div>
        </div>
    );
};
